/**
 * Nexus API Services Page
 * Monitor external API health and balances
 */

import { useEffect, useState } from 'react'
import { nexusAPIServicesApi, APIService } from '@/services/nexusApi'

const serviceTypeIcons: Record<string, string> = {
  voice_tts: '🔊',
  voice_stt: '🎤',
  search: '🔍',
  sms: '📱',
  airtime: '📞',
  ai_model: '🤖',
  video: '🎬',
  image: '🖼️',
}

const healthColors: Record<string, { bg: string; text: string; border: string }> = {
  healthy: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  degraded: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  down: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  unknown: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
}

export default function NexusAPIServices() {
  const [services, setServices] = useState<APIService[]>([])
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState<string | null>(null)
  const [checkingAll, setCheckingAll] = useState(false)

  useEffect(() => {
    loadServices()
  }, [])

  const loadServices = async () => {
    try {
      const data = await nexusAPIServicesApi.list()
      setServices(data.services)
    } catch (error) {
      console.error('Failed to load services:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkBalance = async (name: string) => {
    setChecking(name)
    try {
      await nexusAPIServicesApi.checkBalance(name)
      // Reload to get updated data
      await loadServices()
    } catch (error) {
      console.error('Failed to check balance:', error)
    } finally {
      setChecking(null)
    }
  }

  const checkAllServices = async () => {
    setCheckingAll(true)
    try {
      await nexusAPIServicesApi.checkAll()
      await loadServices()
    } catch (error) {
      console.error('Failed to check all:', error)
    } finally {
      setCheckingAll(false)
    }
  }

  const getHealthColor = (status: string) => healthColors[status] || healthColors.unknown

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-48" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-slate-800 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  // Count by status
  const healthCounts = services.reduce((acc, s) => {
    acc[s.health_status] = (acc[s.health_status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  // Find low balance services
  const lowBalanceServices = services.filter(
    (s) => s.current_balance !== null && 
           s.low_balance_threshold !== null && 
           s.current_balance < s.low_balance_threshold
  )

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">API Services</h1>
          <p className="text-gray-400 mt-1">
            Monitor external API health and balances
          </p>
        </div>
        <button
          onClick={checkAllServices}
          disabled={checkingAll}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {checkingAll ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking...
            </>
          ) : (
            <>🔄 Check All</>
          )}
        </button>
      </div>

      {/* Health Summary */}
      <div className="grid grid-cols-5 gap-4 mb-8">
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-gray-400">Total</p>
          <p className="text-2xl font-bold text-white">{services.length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-green-500/30">
          <p className="text-sm text-green-400">Healthy</p>
          <p className="text-2xl font-bold text-green-400">{healthCounts.healthy || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-yellow-500/30">
          <p className="text-sm text-yellow-400">Degraded</p>
          <p className="text-2xl font-bold text-yellow-400">{healthCounts.degraded || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-red-500/30">
          <p className="text-sm text-red-400">Down</p>
          <p className="text-2xl font-bold text-red-400">{healthCounts.down || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-orange-500/30">
          <p className="text-sm text-orange-400">Low Balance</p>
          <p className="text-2xl font-bold text-orange-400">{lowBalanceServices.length}</p>
        </div>
      </div>

      {/* Low Balance Alert */}
      {lowBalanceServices.length > 0 && (
        <div className="mb-8 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
          <h3 className="text-orange-400 font-medium mb-2">⚠️ Low Balance Alert</h3>
          <p className="text-sm text-gray-300">
            The following services have balance below threshold:{' '}
            {lowBalanceServices.map((s) => s.display_name).join(', ')}
          </p>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {services.map((service) => {
          const colors = getHealthColor(service.health_status)
          const isLowBalance = service.current_balance !== null && 
                               service.low_balance_threshold !== null &&
                               service.current_balance < service.low_balance_threshold
          
          return (
            <div
              key={service.name}
              className={`bg-slate-800 rounded-xl p-5 border ${colors.border} transition-all`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{serviceTypeIcons[service.service_type] || '🔌'}</span>
                  <div>
                    <h3 className="text-white font-medium">{service.display_name}</h3>
                    <p className="text-xs text-gray-500">{service.service_type}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                  {service.health_status}
                </span>
              </div>

              {/* Balance */}
              {service.balance_unit && (
                <div className={`mb-4 p-3 rounded-lg ${isLowBalance ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-slate-700/50'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Balance</span>
                    {isLowBalance && <span className="text-xs text-orange-400">⚠️ Low</span>}
                  </div>
                  <p className={`text-xl font-bold ${isLowBalance ? 'text-orange-400' : 'text-white'}`}>
                    {service.current_balance !== null
                      ? `${service.current_balance.toLocaleString()} ${service.balance_unit}`
                      : 'N/A'}
                  </p>
                  {service.low_balance_threshold && (
                    <p className="text-xs text-gray-500 mt-1">
                      Threshold: {service.low_balance_threshold.toLocaleString()} {service.balance_unit}
                    </p>
                  )}
                </div>
              )}

              {/* Last Check & Actions */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {service.last_health_check
                    ? `Checked: ${new Date(service.last_health_check).toLocaleString()}`
                    : 'Never checked'}
                </p>
                <button
                  onClick={() => checkBalance(service.name)}
                  disabled={checking === service.name}
                  className="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  {checking === service.name ? '...' : 'Check'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
